import { utf8Encode as utf8 } from './bytes.ts';

// SHA-256 (FIPS 180-4), HMAC-SHA-256 (RFC 2104) and PBKDF2 (RFC 8018) in plain TypeScript, synchronous.
// For signing requests in a driver (AWS Signature Version 4) and a login's proof of work (ALTCHA), where
// `crypto.subtle` is not always there: it needs a secure context, and drivers run in Workers on every host.

const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number) : number => (x >>> n) | (x << (32 - n));

const INITIAL_STATE = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];

/** One 64-byte block into `h` (FIPS 180-4 §6.2.2). `w` holds the block's 16 words, and room for 48 more. */
function compress(h: Uint32Array, w: Uint32Array) : void {
    for (let t = 16; t < 64; t++) {
        const s0 = rotr(w[t - 15]!, 7) ^ rotr(w[t - 15]!, 18) ^ (w[t - 15]! >>> 3);
        const s1 = rotr(w[t - 2]!, 17) ^ rotr(w[t - 2]!, 19) ^ (w[t - 2]! >>> 10);
        w[t] = (w[t - 16]! + s0 + w[t - 7]! + s1) >>> 0;
    }

    // Plain locals, not an array: this loop is the cost of PBKDF2.
    let a = h[0]!;
    let b = h[1]!;
    let c = h[2]!;
    let d = h[3]!;
    let e = h[4]!;
    let f = h[5]!;
    let g = h[6]!;
    let hh = h[7]!;
    for (let t = 0; t < 64; t++) {
        const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[t]! + w[t]!) >>> 0;
        const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
        hh = g;
        g = f;
        f = e;
        e = (d + t1) >>> 0;
        d = c;
        c = b;
        b = a;
        a = (t1 + t2) >>> 0;
    }

    h[0] = (h[0]! + a) >>> 0;
    h[1] = (h[1]! + b) >>> 0;
    h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0;
    h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0;
    h[7] = (h[7]! + hh) >>> 0;
}

const digestOf = (h: Uint32Array) : Uint8Array => {
    const digest = new Uint8Array(32);
    const out = new DataView(digest.buffer);
    h.forEach((word, index) => out.setUint32(index * 4, word));

    return digest;
};

/** The SHA-256 digest of `data` (32 bytes); a text is hashed as UTF-8. */
export function sha256(data: Uint8Array | string) : Uint8Array {
    const bytes = typeof data === 'string' ? utf8(data) : data;
    // The message, a 1 bit, zeros, and its length in bits as 64 bits: a whole number of 64-byte blocks.
    const length = Math.ceil((bytes.length + 9) / 64) * 64;
    const padded = new Uint8Array(length);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(length - 8, Math.floor((bytes.length * 8) / 2 ** 32));
    view.setUint32(length - 4, (bytes.length * 8) >>> 0);

    const h = new Uint32Array(INITIAL_STATE);
    const w = new Uint32Array(64);
    for (let offset = 0; offset < length; offset += 64) {
        for (let t = 0; t < 16; t++) {
            w[t] = view.getUint32(offset + t * 4);
        }
        compress(h, w);
    }

    return digestOf(h);
}

/** The HMAC key as a block (a longer key hashed first), XORed with `pad`. */
function keyBlock(key: Uint8Array | string, pad: number) : Uint8Array {
    let keyBytes = typeof key === 'string' ? utf8(key) : key;
    if (keyBytes.length > 64) {
        keyBytes = sha256(keyBytes);
    }

    const block = new Uint8Array(64);
    for (let index = 0; index < 64; index++) {
        block[index] = (keyBytes[index] ?? 0) ^ pad;
    }

    return block;
}

/** HMAC-SHA-256 of `data` with `key` (32 bytes); texts as UTF-8. */
export function hmacSha256(key: Uint8Array | string, data: Uint8Array | string) : Uint8Array {
    const dataBytes = typeof data === 'string' ? utf8(data) : data;
    const inner = new Uint8Array(64 + dataBytes.length);
    inner.set(keyBlock(key, 0x36));
    inner.set(dataBytes, 64);
    const outer = new Uint8Array(64 + 32);
    outer.set(keyBlock(key, 0x5c));
    outer.set(sha256(inner), 64);

    return sha256(outer);
}

/**
 * PBKDF2 with HMAC-SHA-256 (RFC 8018 §5.2): `length` bytes from `password` and `salt`, texts as UTF-8.
 * Each round hashes only the two 64-byte blocks after the key's, from states worked out once.
 */
export function pbkdf2Sha256(password: Uint8Array | string, salt: Uint8Array | string, iterations: number, length: number) : Uint8Array {
    const saltBytes = typeof salt === 'string' ? utf8(salt) : salt;
    const keyState = (pad: number) : Uint32Array => {
        const block = new DataView(keyBlock(password, pad).buffer);
        const w = new Uint32Array(64);
        for (let t = 0; t < 16; t++) {
            w[t] = block.getUint32(t * 4);
        }
        const state = new Uint32Array(INITIAL_STATE);
        compress(state, w);

        return state;
    };
    const innerKey = keyState(0x36);
    const outerKey = keyState(0x5c);

    // A 32-byte message after a key block: the 1 bit, then the length, (64 + 32) * 8 bits.
    const w = new Uint32Array(64);
    const state = new Uint32Array(8);
    const hmacOfDigest = (u: Uint32Array) : void => {
        for (const key of [innerKey, outerKey]) {
            w.set(u);
            w[8] = 0x80000000;
            w.fill(0, 9, 15);
            w[15] = 768;
            state.set(key);
            compress(state, w);
            u.set(state);
        }
    };

    const derived = new Uint8Array(Math.ceil(length / 32) * 32);
    for (let block = 1; block * 32 - 32 < length; block++) {
        const first = new Uint8Array(saltBytes.length + 4);
        first.set(saltBytes);
        new DataView(first.buffer).setUint32(saltBytes.length, block);
        const firstDigest = new DataView(hmacSha256(password, first).buffer);

        const u = new Uint32Array(8);
        for (let index = 0; index < 8; index++) {
            u[index] = firstDigest.getUint32(index * 4);
        }
        const sum = new Uint32Array(u);
        for (let round = 1; round < iterations; round++) {
            hmacOfDigest(u);
            for (let index = 0; index < 8; index++) {
                sum[index] = (sum[index]! ^ u[index]!) >>> 0;
            }
        }

        derived.set(digestOf(sum), (block - 1) * 32);
    }

    return derived.slice(0, length);
}

/** Bytes as lower-case hex. */
export const toHex = (bytes: Uint8Array) : string => Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
